import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, TextInput, Platform, Modal } from 'react-native';
import { openExternal } from '../../../src/utils/openExternal';
import { useRouter, useLocalSearchParams, useFocusEffect, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../../src/api/client';
import { config } from '../../../src/config';
import { AdminToolbar } from '../../../src/components/AdminList';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Picker from '../../../src/components/Picker';
import { useToast } from '../../../src/components/Toast';
import InlineConfirm from '../../../src/components/InlineConfirm';
import InlineMessage from '../../../src/components/InlineMessage';
import RefundDialog from '../../../src/components/RefundDialog';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import { useNetwork } from '../../../src/context/NetworkContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { titleCase, formatPhone } from '../../../src/utils/format';

interface FleetAccount {
  id: string;
  companyName: string;
  status: string;
  billingEmail: string;
  billingAddress: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingZip: string | null;
  paymentTermsDays: number;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  taxId: string | null;
  monthlySpendingLimit: number | null;
  pricingTierId: string | null;
  pricingTier: { tierName: string } | null;
  invoiceAutoGeneration: string;
  autoGenFrequency: string | null;
  autoGenDayOfWeek: number | null;
}

interface TransactionRow {
  transaction: {
    id: string;
    washType: string;
    retailPrice: number;
    refundedAmount?: number;
    status: string;
    createdAt: string;
    userName: string | null;
    userEmail: string | null;
    userPhone: string | null;
  };
  vehicle: { licensePlate: string } | null;
  location: { name: string } | null;
  bay: { name: string } | null;
  effectiveDiscountPercentage: number;
  discountedAmount: number;
  billing: 'paid' | 'invoiced' | 'unbilled';
  invoiceNumber: string | null;
}

interface Transaction {
  id: string;
  washType: string;
  retailPrice: number;
  refundedAmount: number;
  createdAt: string;
  locationName: string;
  bayName: string;
  status: string;
  userName: string | null;
  userEmail: string | null;
  userPhone: string | null;
  licensePlate: string | null;
  discountPercent: number;
  discountedAmount: number;
  billing: 'paid' | 'invoiced' | 'unbilled';
  invoiceNumber: string | null;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  status: string;
  subtotal: number;
  discountPercentage: number | null;
  discountAmount: number;
  totalAmount: number;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  sentAt: string | null;
  paidAt: string | null;
  paymentMethod: string | null;
  paymentReference: string | null;
}

/** Method choices for manually marking an invoice paid (check in the mail, cash in hand, …). */
const MARK_PAID_METHODS = ['check', 'cash', 'bank transfer', 'card (in person)', 'other'] as const;

interface FleetMember {
  id: string;
  userId: string;
  role: string;
  name: string;
  accountNumber: string;
  createdAt: string;
}

interface SearchResult {
  id: string;
  name: string;
  accountNumber: string;
}

interface PricingTier {
  id: string;
  tierName: string;
}

type Tab = 'transactions' | 'invoices' | 'members';

/** FleetAccountDetailScreen - fleet account details, transactions, invoices, and members. */
export default function FleetAccountDetailScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const { id } = useLocalSearchParams();
  const [account, setAccount] = useState<FleetAccount | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invTotal, setInvTotal] = useState(0);
  const [invPage, setInvPage] = useState(1);
  const [invSearch, setInvSearch] = useState('');
  const [activeCount, setActiveCount] = useState(0);
  const [historyCount, setHistoryCount] = useState(0);
  const [members, setMembers] = useState<FleetMember[]>([]);
  const [managers, setManagers] = useState<FleetMember[]>([]);
  const [memberTotal, setMemberTotal] = useState(0);
  const [memberPage, setMemberPage] = useState(1);
  const [memberSearch, setMemberSearch] = useState('');
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('transactions');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Record<string, string | number | null>>({});
  const [saving, setSaving] = useState(false);

  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);
  // "Email…" inline prompt (send invoice to a specific address)
  const [emailTo, setEmailTo] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [invoiceHtml, setInvoiceHtml] = useState<string | null>(null);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);
  const [markPaidMethod, setMarkPaidMethod] = useState<string>('check');
  const [markPaidRef, setMarkPaidRef] = useState('');
  const [invoiceView, setInvoiceView] = useState<'active' | 'history'>('active');

  // Inline confirm/message state
  const [confirmAction, setConfirmAction] = useState<{ type: string; id?: string; data?: any } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Transaction search
  const [txSearch, setTxSearch] = useState('');

  // Manager search
  const [managerSearch, setManagerSearch] = useState('');
  const [managerResults, setManagerResults] = useState<SearchResult[]>([]);
  const [searchingManager, setSearchingManager] = useState(false);

  const router = useRouter();
  const toast = useToast();
  const { isOffline } = useNetwork();

  const mapTxRow = (row: TransactionRow): Transaction => ({
    id: row.transaction.id,
    washType: row.transaction.washType,
    retailPrice: row.transaction.retailPrice,
    refundedAmount: row.transaction.refundedAmount ?? 0,
    createdAt: row.transaction.createdAt,
    locationName: row.location?.name || 'Unknown',
    bayName: row.bay?.name || '',
    status: row.transaction.status || 'completed',
    userName: row.transaction.userName ?? null,
    userEmail: row.transaction.userEmail ?? null,
    userPhone: row.transaction.userPhone ?? null,
    licensePlate: row.vehicle?.licensePlate ?? null,
    discountPercent: row.effectiveDiscountPercentage ?? 0,
    discountedAmount: row.discountedAmount ?? row.transaction.retailPrice,
    billing: row.billing ?? 'unbilled',
    invoiceNumber: row.invoiceNumber ?? null,
  });

  const fetchTransactions = async (p = txPage, searchOverride?: string) => {
    const params = new URLSearchParams({ page: String(p), limit: '10' });
    const s = searchOverride ?? txSearch;
    if (s) params.set('search', s);
    const data = await apiGet<{ transactions: TransactionRow[]; total: number }>(`/api/fleet/accounts/${id}/transactions?${params}`);
    setTransactions(data.transactions.map(mapTxRow));
    setTxTotal(data.total);
    setTxPage(p);
  };

  const fetchInvoices = async (p = invPage, view = invoiceView, searchOverride?: string) => {
    const params = new URLSearchParams({ page: String(p), limit: '10', view });
    const s = searchOverride ?? invSearch;
    if (s) params.set('search', s);
    const data = await apiGet<{ invoices: Invoice[]; total: number; activeCount: number; historyCount: number }>(`/api/fleet/accounts/${id}/invoices?${params}`);
    setInvoices(data.invoices);
    setInvTotal(data.total);
    setActiveCount(data.activeCount);
    setHistoryCount(data.historyCount);
    setInvPage(p);
  };

  const fetchMembers = async (p = memberPage, searchOverride?: string) => {
    const params = new URLSearchParams({ page: String(p), limit: '10', role: 'member' });
    const s = searchOverride ?? memberSearch;
    if (s) params.set('search', s);
    const data = await apiGet<{ members: FleetMember[]; total: number }>(`/api/fleet/accounts/${id}/members?${params}`);
    setMembers(data.members);
    setMemberTotal(data.total);
    setMemberPage(p);
  };

  const fetchData = async () => {
    let failures = 0;
    const track = <T,>(p: Promise<T>, fallback: T): Promise<T> => p.catch(() => { failures++; return fallback; });
    try {
      setLoading(true);
      const [accountData, managersData, tiersData] = await Promise.all([
        apiGet<FleetAccount>(`/api/fleet/accounts/${id}`),
        track(apiGet<FleetMember[]>(`/api/fleet/accounts/${id}/members?role=manager`), [] as FleetMember[]),
        track(apiGet<PricingTier[]>('/api/fleet/pricing-tiers'), [] as PricingTier[]),
      ]);
      await Promise.all([
        track(fetchTransactions(), undefined),
        track(fetchInvoices(), undefined),
        track(fetchMembers(), undefined),
      ]);

      setAccount(accountData);
      setManagers(managersData);
      setTiers(tiersData);
      if (failures > 0 && !isOffline) {
        toast.show({ type: 'warning', title: 'Some account data didn’t load', message: `${failures} section${failures === 1 ? '' : 's'} failed to refresh.` });
      }
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchData(); fetchManagerInvites(); }, [id]));


  const handleSuspend = () => {
    setConfirmAction({ type: 'suspend' });
  };

  const doSuspend = async () => {
    setConfirmLoading(true);
    try {
      await apiPatch(`/api/fleet/accounts/${id}`, { status: 'suspended' });
      setConfirmAction(null);
      setInlineMsg({ type: 'success', message: 'Account suspended' });
      fetchData();
    } catch (err: any) {
      setConfirmAction(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      await apiPatch(`/api/fleet/accounts/${id}`, { status: 'active' });
      toast.show({ type: 'success', title: 'Success', message: 'Account activated' });
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleDelete = () => {
    setConfirmAction({ type: 'delete' });
  };

  const doDelete = async () => {
    setConfirmLoading(true);
    try {
      await apiDelete(`/api/fleet/accounts/${id}`);
      setConfirmAction(null);
      toast.show({ type: 'success', title: 'Deleted', message: 'Account has been cancelled' });
      router.back();
    } catch (err: any) {
      setConfirmAction(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setConfirmLoading(false);
    }
  };

  const startEdit = () => {
    if (!account) return;
    setEditData({
      companyName: account.companyName,
      billingEmail: account.billingEmail,
      paymentTermsDays: account.paymentTermsDays,
      pricingTierId: account.pricingTierId ?? '',
      primaryContactName: account.primaryContactName ?? '',
      primaryContactPhone: account.primaryContactPhone ?? '',
      primaryContactEmail: account.primaryContactEmail ?? '',
      billingAddress: account.billingAddress ?? '',
      billingCity: account.billingCity ?? '',
      billingState: account.billingState ?? '',
      billingZip: account.billingZip ?? '',
      taxId: account.taxId ?? '',
      monthlySpendingLimit: account.monthlySpendingLimit,
      invoiceAutoGeneration: account.invoiceAutoGeneration ?? 'off',
      autoGenFrequency: account.autoGenFrequency ?? 'monthly',
      autoGenDayOfWeek: account.autoGenDayOfWeek != null ? String(account.autoGenDayOfWeek) : '1',
    });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {};
      if (editData.companyName) payload.companyName = editData.companyName;
      if (editData.billingEmail) payload.billingEmail = editData.billingEmail;
      if (editData.paymentTermsDays !== undefined && editData.paymentTermsDays !== null) {
        const n = Number(editData.paymentTermsDays);
        if (Number.isFinite(n) && n >= 0 && n <= 365) payload.paymentTermsDays = Math.trunc(n);
      }
      if (editData.primaryContactName !== undefined) payload.primaryContactName = editData.primaryContactName || undefined;
      if (editData.primaryContactPhone !== undefined) payload.primaryContactPhone = editData.primaryContactPhone || undefined;
      if (editData.primaryContactEmail !== undefined) payload.primaryContactEmail = editData.primaryContactEmail || undefined;
      if (editData.billingAddress !== undefined) payload.billingAddress = editData.billingAddress || undefined;
      if (editData.billingCity !== undefined) payload.billingCity = editData.billingCity || undefined;
      if (editData.billingState !== undefined) payload.billingState = editData.billingState || undefined;
      if (editData.billingZip !== undefined) payload.billingZip = editData.billingZip || undefined;
      if (editData.taxId !== undefined) payload.taxId = editData.taxId || undefined;
      if (editData.pricingTierId) payload.pricingTierId = editData.pricingTierId;
      if (editData.monthlySpendingLimit !== null && editData.monthlySpendingLimit !== undefined) {
        payload.monthlySpendingLimit = Number(editData.monthlySpendingLimit);
      }
      if (editData.invoiceAutoGeneration) payload.invoiceAutoGeneration = editData.invoiceAutoGeneration;
      if (editData.invoiceAutoGeneration && editData.invoiceAutoGeneration !== 'off') {
        payload.autoGenFrequency = editData.autoGenFrequency || 'monthly';
        if (payload.autoGenFrequency === 'weekly' || payload.autoGenFrequency === 'biweekly') {
          const dow = parseInt(String(editData.autoGenDayOfWeek ?? '1'), 10);
          payload.autoGenDayOfWeek = Number.isFinite(dow) && dow >= 0 && dow <= 6 ? dow : 1;
        } else {
          payload.autoGenDayOfWeek = null;
        }
      } else {
        payload.autoGenFrequency = null;
        payload.autoGenDayOfWeek = null;
      }
      await apiPatch(`/api/fleet/accounts/${id}`, payload);
      toast.show({ type: 'success', title: 'Saved', message: 'Account updated' });
      setEditing(false);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const managerList = managers;
  const [managerInvites, setManagerInvites] = useState<{ id: string; email: string; expiresAt: string }[]>([]);
  const [inviting, setInviting] = useState(false);

  const fetchManagerInvites = async () => {
    try {
      const inv = await apiGet<{ id: string; email: string; expiresAt: string }[]>(`/api/fleet/accounts/${id}/manager-invites`);
      setManagerInvites(inv);
    } catch { /* non-fatal */ }
  };

  const handleInviteManager = async (email: string) => {
    setInviting(true);
    try {
      const res = await apiPost<{ added: boolean; invited: boolean }>(`/api/fleet/accounts/${id}/manager-invite`, { email });
      toast.show({
        type: 'success',
        title: res.added ? 'Manager added' : 'Invite sent',
        message: res.added ? `${email} is now a manager.` : `${email} will become a manager when they create their account.`,
      });
      setManagerSearch('');
      setManagerResults([]);
      fetchData();
      fetchManagerInvites();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setInviting(false); }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await apiDelete(`/api/fleet/accounts/${id}/manager-invites/${inviteId}`);
      fetchManagerInvites();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleSearchManager = async (q: string) => {
    setManagerSearch(q);
    if (q.length < 2) { setManagerResults([]); return; }
    setSearchingManager(true);
    try {
      const results = await apiGet<SearchResult[]>(`/api/fleet/search-customers?q=${encodeURIComponent(q)}`);
      setManagerResults(results);
    } catch { setManagerResults([]); }
    finally { setSearchingManager(false); }
  };

  const handleAssignManager = async (userId: string) => {
    try {
      await apiPost(`/api/fleet/accounts/${id}/manager`, { userId });
      toast.show({ type: 'success', title: 'Success', message: 'Manager assigned' });
      setManagerSearch('');
      setManagerResults([]);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleRemoveManager = (member: FleetMember) => {
    setConfirmAction({ type: 'removeManager', id: member.userId, data: member });
  };

  const doRemoveManager = async () => {
    if (!confirmAction?.id) return;
    setConfirmLoading(true);
    try {
      await apiDelete(`/api/fleet/accounts/${id}/manager`, { body: { userId: confirmAction.id } });
      setConfirmAction(null);
      setInlineMsg({ type: 'success', message: 'Manager removed' });
      fetchData();
    } catch (err: any) {
      setConfirmAction(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handlePromoteMember = async (member: FleetMember) => {
    try {
      await apiPost(`/api/fleet/accounts/${id}/manager`, { userId: member.userId });
      setInlineMsg({ type: 'success', message: `${titleCase(member.name)} is now a manager` });
      fetchData();
    } catch (err: any) {
      setInlineMsg({ type: 'error', message: err.message });
    }
  };

  const handleRemoveMember = (member: FleetMember) => {
    setConfirmAction({ type: 'removeMember', id: member.userId, data: member });
  };

  const doRemoveMember = async () => {
    if (!confirmAction?.id) return;
    setConfirmLoading(true);
    try {
      await apiDelete(`/api/fleet/accounts/${id}/members/${confirmAction.id}`);
      const memberName = titleCase(confirmAction.data?.name) || 'Member';
      setConfirmAction(null);
      setInlineMsg({ type: 'success', message: `${memberName} removed` });
      fetchData();
    } catch (err: any) {
      setConfirmAction(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleRefund = (tx: Transaction) => {
    setConfirmAction({ type: 'refund', id: tx.id, data: tx });
  };

  const doRefund = async (amount: number) => {
    if (!confirmAction?.id) return;
    setConfirmLoading(true);
    try {
      await apiPost(`/api/fleet/accounts/${id}/transactions/${confirmAction.id}/refund`, { amount });
      setConfirmAction(null);
      setInlineMsg({ type: 'success', message: `${fmt(amount)} refunded` });
      fetchData();
    } catch (err: any) {
      setConfirmAction(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDeleteInvoice = (invoice: Invoice) => {
    setConfirmAction({ type: 'deleteInvoice', id: invoice.id, data: invoice });
  };

  const doDeleteInvoice = async () => {
    if (!confirmAction?.id) return;
    setConfirmLoading(true);
    try {
      await apiDelete(`/api/fleet/invoices/${confirmAction.id}`);
      setConfirmAction(null);
      setInlineMsg({ type: 'success', message: 'Invoice deleted' });
      fetchData();
    } catch (err: any) {
      setConfirmAction(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleSendInvoice = (invoice: Invoice) => {
    setConfirmAction({ type: 'sendInvoice', id: invoice.id, data: invoice });
  };

  const doSendInvoice = async () => {
    if (!confirmAction?.id) return;
    setConfirmLoading(true);
    setSendingInvoiceId(confirmAction.id);
    try {
      await apiPost(`/api/fleet/invoices/${confirmAction.id}/send`, {});
      setConfirmAction(null);
      setInlineMsg({ type: 'success', message: 'Invoice emailed successfully' });
      fetchData();
    } catch (err: any) {
      setConfirmAction(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setConfirmLoading(false);
      setSendingInvoiceId(null);
    }
  };

  const handleEmailInvoice = (invoice: Invoice) => {
    setEmailTo('');
    setEmailError(null);
    setConfirmAction({ type: 'emailInvoice', id: invoice.id, data: invoice });
  };

  const doEmailInvoice = async () => {
    if (!confirmAction?.id) return;
    const email = emailTo.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setEmailError('Enter a valid email address');
      return;
    }
    setEmailError(null);
    setConfirmLoading(true);
    setSendingInvoiceId(confirmAction.id);
    try {
      await apiPost(`/api/fleet/invoices/${confirmAction.id}/send`, { to: email });
      setConfirmAction(null);
      setInlineMsg({ type: 'success', message: `Invoice emailed to ${email}` });
      fetchData();
    } catch (err: any) {
      setConfirmAction(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setConfirmLoading(false);
      setSendingInvoiceId(null);
    }
  };

  /** Open printable invoice HTML in modal (web) or browser (native) */
  const handleViewInvoice = async (invoice: Invoice) => {
    try {
      const res = await apiPost(`/api/fleet/invoices/${invoice.id}/view-token`, {});
      const { viewToken } = res as { viewToken: string };
      const url = `${config.apiUrl}/api/fleet/invoices/${invoice.id}/html?token=${viewToken}`;
      if (Platform.OS === 'web') {
        const resp = await fetch(url);
        const html = await resp.text();
        setInvoiceHtml(html.replace(/window\.print\(\)/g, `window.open('${url}','_blank')`));
        setInvoiceUrl(url);
      } else {
        openExternal(url);
      }
    } catch {
      toast.show({ type: 'error', title: 'Error', message: 'Failed to open invoice' });
    }
  };

  const handleMarkPaid = (invoice: Invoice) => {
    setMarkPaidMethod('check');
    setMarkPaidRef('');
    setConfirmAction({ type: 'markPaid', id: invoice.id, data: invoice });
  };

  const doMarkPaid = async () => {
    if (!confirmAction?.id) return;
    setConfirmLoading(true);
    setMarkingPaidId(confirmAction.id);
    try {
      await apiPatch(`/api/fleet/invoices/${confirmAction.id}/mark-paid`, {
        paymentMethod: markPaidMethod,
        ...(markPaidRef.trim() ? { paymentReference: markPaidRef.trim() } : {}),
      });
      setConfirmAction(null);
      setInlineMsg({ type: 'success', message: 'Invoice marked as paid' });
      fetchData();
    } catch (err: any) {
      setConfirmAction(null);
      setInlineMsg({ type: 'error', message: err.message });
    } finally {
      setConfirmLoading(false);
      setMarkingPaidId(null);
    }
  };

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const formatDate = (dateStr: string, includeTime = false) => {
    const date = new Date(dateStr);
    const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (!includeTime) return datePart;
    const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${datePart} ${timePart}`;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return colors.success;
      case 'suspended': return colors.warning;
      case 'cancelled': return colors.danger;
      case 'paid': return colors.success;
      case 'pending': return colors.warning;
      case 'sent': return colors.primary;
      case 'overdue': return colors.danger;
      default: return colors.textSecondary;
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!account) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.errorText}>Account not found</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <ContentContainer>
      <Stack.Screen options={{ title: account.companyName }} />
      {/* Account Info */}
      <Card style={styles.infoCard}>
        <View style={styles.infoHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.companyName}>{account.companyName}</Text>
            {account.pricingTier?.tierName ? (
              <Text style={styles.fleetType}>{account.pricingTier.tierName}</Text>
            ) : null}
          </View>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(account.status) + '20' }]}>
            <Text style={[styles.statusText, { color: getStatusColor(account.status) }]}>
              {account.status}
            </Text>
          </View>
        </View>

        {editing ? (
          <View style={styles.editForm}>
            {([
              ['companyName', 'Company Name'],
              ['billingEmail', 'Billing Email'],
              ['paymentTermsDays', 'Net days until due'],
              ['primaryContactName', 'Contact Name (info only)'],
              ['primaryContactPhone', 'Contact Phone (info only)'],
              ['primaryContactEmail', 'Contact Email (info only)'],
              ['billingAddress', 'Billing Address'],
              ['billingCity', 'City'],
              ['billingState', 'State'],
              ['billingZip', 'Zip'],
              ['taxId', 'Tax ID'],
            ] as const).map(([key, label]) => (
              <View key={key} style={styles.editField}>
                <Text style={styles.editLabel}>{label}</Text>
                <TextInput
                  style={styles.editInput}
                  value={String(editData[key] ?? '')}
                  onChangeText={(v) => setEditData(prev => ({ ...prev, [key]: v }))}
                  placeholder={label}
                  placeholderTextColor={colors.textLight}
                  maxLength={100}
                />
              </View>
            ))}
            {tiers.length > 0 && (
              <Picker
                label="Pricing Tier"
                value={String(editData.pricingTierId ?? '')}
                onValueChange={(v) => setEditData(prev => ({ ...prev, pricingTierId: v }))}
                options={[
                  { label: 'None', value: '' },
                  ...tiers.map(t => ({ label: t.tierName, value: t.id })),
                ]}
              />
            )}
            <Picker
              label="Auto Invoicing"
              value={String(editData.invoiceAutoGeneration ?? 'off')}
              onValueChange={(v) => setEditData(prev => ({ ...prev, invoiceAutoGeneration: v }))}
              options={[
                { label: 'Off', value: 'off' },
                { label: 'Generate & Notify Me', value: 'generate_notify' },
                { label: 'Generate & Auto-Send', value: 'generate_send' },
              ]}
            />
            {editData.invoiceAutoGeneration && editData.invoiceAutoGeneration !== 'off' && (
              <>
                <Picker
                  label="Frequency"
                  value={String(editData.autoGenFrequency ?? 'monthly')}
                  onValueChange={(v) => setEditData(prev => ({ ...prev, autoGenFrequency: v }))}
                  options={[
                    { label: 'Weekly (prior 7 days)', value: 'weekly' },
                    { label: 'Biweekly (prior 14 days)', value: 'biweekly' },
                    { label: 'Monthly (1st of month, prior full month)', value: 'monthly' },
                  ]}
                />
                {(editData.autoGenFrequency === 'weekly' || editData.autoGenFrequency === 'biweekly') && (
                  <Picker
                    label="Run Day"
                    value={String(editData.autoGenDayOfWeek ?? '1')}
                    onValueChange={(v) => setEditData(prev => ({ ...prev, autoGenDayOfWeek: v }))}
                    options={[
                      { label: 'Sunday', value: '0' },
                      { label: 'Monday', value: '1' },
                      { label: 'Tuesday', value: '2' },
                      { label: 'Wednesday', value: '3' },
                      { label: 'Thursday', value: '4' },
                      { label: 'Friday', value: '5' },
                      { label: 'Saturday', value: '6' },
                    ]}
                  />
                )}
              </>
            )}
            <View style={styles.editActions}>
              <Button title="Cancel" onPress={() => setEditing(false)} variant="outline" size="sm" style={{ flex: 1 }} />
              <Button title={saving ? 'Saving...' : 'Save'} onPress={handleSaveEdit} size="sm" style={{ flex: 1 }} disabled={saving} />
            </View>
          </View>
        ) : (
          <View style={styles.infoGrid}>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Pricing Tier</Text>
              <Text style={styles.infoValue}>{account.pricingTier?.tierName || 'N/A'}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Payment Terms</Text>
              <Text style={styles.infoValue}>Net {account.paymentTermsDays} days</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Billing Email</Text>
              <Text style={styles.infoValue}>{account.billingEmail}</Text>
            </View>
            {account.primaryContactName && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>Contact</Text>
                <Text style={styles.infoValue}>{titleCase(account.primaryContactName)}</Text>
              </View>
            )}
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Auto Invoicing</Text>
              <Text style={styles.infoValue}>
                {account.invoiceAutoGeneration === 'generate_notify' ? 'Generate & Notify' :
                 account.invoiceAutoGeneration === 'generate_send' ? 'Generate & Auto-Send' : 'Off'}
                {account.invoiceAutoGeneration !== 'off' && account.autoGenFrequency
                  ? (() => {
                      const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                      const dn = account.autoGenDayOfWeek != null ? dayNames[account.autoGenDayOfWeek] : 'Mon';
                      if (account.autoGenFrequency === 'monthly') return ' — 1st of month for prior month';
                      if (account.autoGenFrequency === 'biweekly') return ` — every other ${dn}`;
                      return ` — every ${dn}`;
                    })()
                  : ''}
              </Text>
            </View>
          </View>
        )}
      </Card>

      {/* Action Buttons */}
      <View style={styles.actions}>
        {account.status === 'active' ? (
          <Button
            title="Suspend"
            onPress={handleSuspend}
            variant="danger"
            size="sm"
            style={{ flex: 1 }}
          />
        ) : account.status !== 'cancelled' ? (
          <Button
            title="Activate"
            onPress={handleActivate}
            variant="secondary"
            size="sm"
            style={{ flex: 1 }}
          />
        ) : null}
        <Button
          title="Edit"
          onPress={startEdit}
          variant="outline"
          size="sm"
          style={{ flex: 1 }}
        />
        <Button
          title="Delete"
          onPress={handleDelete}
          variant="danger"
          size="sm"
          style={{ flex: 1 }}
        />
      </View>
      <InlineConfirm
        message={`Suspend ${account.companyName}? Fleet will be unable to use services until reactivated.`}
        visible={confirmAction?.type === 'suspend'}
        onConfirm={doSuspend}
        onCancel={() => setConfirmAction(null)}
        confirmLabel="Suspend"
        loading={confirmLoading}
        variant="warning"
      />
      <InlineConfirm
        message={`Delete ${account.companyName}? This will cancel the account.`}
        visible={confirmAction?.type === 'delete'}
        onConfirm={doDelete}
        onCancel={() => setConfirmAction(null)}
        confirmLabel="Delete"
        loading={confirmLoading}
        variant="danger"
      />
      <InlineMessage
        type={inlineMsg?.type || 'success'}
        message={inlineMsg?.message || ''}
        visible={!!inlineMsg}
        onDismiss={() => setInlineMsg(null)}
        autoDismissMs={4000}
      />

      <View style={styles.actions}>
        <Button
          title="Generate Invoice"
          onPress={() => router.push(`/owner/fleet/generate-invoice?fleetId=${id}` as never)}
          variant="outline"
          size="sm"
          style={{ flex: 1 }}
        />
      </View>

      {/* Tabs */}
      <View style={styles.tabs}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'transactions' && styles.activeTab]}
          onPress={() => setActiveTab('transactions')}
        >
          <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7} style={[styles.tabText, activeTab === 'transactions' && styles.activeTabText]}>
            Transactions ({txTotal})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'invoices' && styles.activeTab]}
          onPress={() => setActiveTab('invoices')}
        >
          <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7} style={[styles.tabText, activeTab === 'invoices' && styles.activeTabText]}>
            Invoices ({activeCount + historyCount})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'members' && styles.activeTab]}
          onPress={() => setActiveTab('members')}
        >
          <Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7} style={[styles.tabText, activeTab === 'members' && styles.activeTabText]}>
            Members ({managers.length + memberTotal})
          </Text>
        </TouchableOpacity>
      </View>

      {/* Tab Content */}
      {activeTab === 'transactions' && (
        <>
          <AdminToolbar
            search={txSearch}
            onSearchChange={setTxSearch}
            onSearch={() => fetchTransactions(1).catch(() => {})}
            total={txTotal}
            page={txPage}
            limit={10}
            onPageChange={(p) => fetchTransactions(p).catch(() => {})}
            csvEndpoint={`/api/fleet/accounts/${id}/transactions${txSearch ? `?search=${encodeURIComponent(txSearch)}` : ''}`}
            csvFilename="fleet-transactions.csv"
            label="transaction"
          />
          {transactions.length > 0 ? (
            <Card style={styles.txListCard}>
              {transactions.map((tx, idx) => {
                const fullyRefunded = tx.status === 'refunded' || tx.refundedAmount >= tx.retailPrice;
                const partiallyRefunded = !fullyRefunded && tx.refundedAmount > 0;
                return (
                <View key={tx.id}>
                  <View style={[
                    styles.txRow,
                    fullyRefunded && styles.refundedCard,
                    idx < transactions.length - 1 && confirmAction?.type !== 'refund' && styles.txRowBorder,
                  ]}>
                    <View style={styles.txLeft}>
                      <Text style={styles.txType} numberOfLines={1}>{tx.washType}{tx.bayName ? ` — ${tx.bayName}` : ''}</Text>
                      <Text style={styles.txSub} numberOfLines={1}>{tx.locationName} · {formatDate(tx.createdAt, true)}</Text>
                      {(tx.userName || tx.userEmail || tx.userPhone) ? (
                        <Text style={styles.txSub} numberOfLines={1}>
                          {[tx.userName ? titleCase(tx.userName) : null, tx.userEmail, tx.userPhone ? formatPhone(tx.userPhone) : null].filter(Boolean).join(' · ')}
                        </Text>
                      ) : null}
                      {tx.licensePlate ? (
                        <Text style={styles.txSub} numberOfLines={1}>Plate: {tx.licensePlate}</Text>
                      ) : null}
                      <Text style={styles.txSub} numberOfLines={1}>
                        {tx.billing === 'paid' ? `Paid${tx.invoiceNumber ? ` · ${tx.invoiceNumber}` : ''}`
                          : tx.billing === 'invoiced' ? `Invoiced${tx.invoiceNumber ? ` · ${tx.invoiceNumber}` : ''}`
                          : 'Unbilled'}
                      </Text>
                    </View>
                    <View style={styles.txRight}>
                      <Text style={[
                        styles.txAmount,
                        fullyRefunded && styles.refundedAmount,
                      ]}>{fmt(tx.discountedAmount)}</Text>
                      {tx.discountPercent > 0 && !fullyRefunded && (
                        <Text style={styles.txSub}>{fmt(tx.retailPrice)} retail · {tx.discountPercent}% fleet</Text>
                      )}
                      {fullyRefunded ? (
                        <View style={styles.refundedBadge}>
                          <Text style={styles.refundedBadgeText}>Refunded</Text>
                        </View>
                      ) : (
                        <TouchableOpacity onPress={() => handleRefund(tx)} style={styles.refundBtn}>
                          <Ionicons name="return-down-back" size={14} color={colors.danger} />
                        </TouchableOpacity>
                      )}
                      {partiallyRefunded && (
                        <Text style={styles.refundedBadgeText}>{fmt(tx.refundedAmount)} refunded</Text>
                      )}
                    </View>
                  </View>
                  {confirmAction?.type === 'refund' && confirmAction.id === tx.id && (
                    <View style={{ paddingHorizontal: spacing.md, paddingBottom: spacing.sm }}>
                      <RefundDialog
                        visible={true}
                        originalCents={tx.retailPrice}
                        alreadyRefundedCents={tx.refundedAmount}
                        subtitle={`Refund this ${tx.washType} wash?`}
                        loading={confirmLoading}
                        onConfirm={(amount) => doRefund(amount)}
                        onCancel={() => setConfirmAction(null)}
                      />
                    </View>
                  )}
                </View>
                );
              })}
            </Card>
          ) : (
            <Card style={styles.emptyCard}>
              <Text style={styles.emptyText}>{txSearch.trim() ? 'No transactions match your search' : 'No transactions yet'}</Text>
            </Card>
          )}
        </>
      )}

      {activeTab === 'invoices' && (
        <>
          <AdminToolbar
            search={invSearch}
            onSearchChange={setInvSearch}
            onSearch={() => fetchInvoices(1).catch(() => {})}
            total={invTotal}
            page={invPage}
            limit={10}
            onPageChange={(p) => fetchInvoices(p).catch(() => {})}
            csvEndpoint={`/api/fleet/accounts/${id}/invoices?view=${invoiceView}${invSearch ? `&search=${encodeURIComponent(invSearch)}` : ''}`}
            csvFilename="fleet-invoices.csv"
            label="invoice"
          />
          {/* Sub-tabs: Active / History */}
          <View style={styles.subTabs}>
            <TouchableOpacity
              style={[styles.subTab, invoiceView === 'active' && styles.subTabActive]}
              onPress={() => { setInvoiceView('active'); fetchInvoices(1, 'active').catch(() => {}); }}
            >
              <Text style={[styles.subTabText, invoiceView === 'active' && styles.subTabTextActive]}>
                Active ({activeCount})
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.subTab, invoiceView === 'history' && styles.subTabActive]}
              onPress={() => { setInvoiceView('history'); fetchInvoices(1, 'history').catch(() => {}); }}
            >
              <Text style={[styles.subTabText, invoiceView === 'history' && styles.subTabTextActive]}>
                History ({historyCount})
              </Text>
            </TouchableOpacity>
          </View>

          {invoiceView === 'active' && (
            invoices.length > 0 ? (
              invoices.map(invoice => (
                <Card key={invoice.id} style={styles.itemCard}>
                    <View>
                      <View style={styles.invoiceRow}>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.invoiceNumber}>#{invoice.invoiceNumber}</Text>
                          <Text style={styles.invoicePeriod}>
                            {formatDate(invoice.billingPeriodStart)} - {formatDate(invoice.billingPeriodEnd)}
                          </Text>
                          {invoice.paidAt ? (
                            <Text style={{ fontSize: fontSize.xs, color: colors.success, marginTop: 2 }}>
                              Paid {formatDate(invoice.paidAt)}{invoice.paymentMethod ? ` via ${invoice.paymentMethod}` : ''}{invoice.paymentReference ? ` (${invoice.paymentReference})` : ''}
                            </Text>
                          ) : null}
                        </View>
                        <View style={{ alignItems: 'flex-end' }}>
                          <Text style={styles.invoiceAmount}>{fmt(invoice.totalAmount)}</Text>
                          {(invoice.discountAmount || 0) > 0 && (
                            <View style={{ alignItems: 'flex-end', marginTop: 2 }}>
                              <Text style={styles.invoiceSaved}>
                                Discount{invoice.discountPercentage ? ` (${invoice.discountPercentage}%)` : ''}: -{fmt(invoice.discountAmount)}
                              </Text>
                            </View>
                          )}
                          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(invoice.status) + '20', marginTop: spacing.xs }]}>
                            <Text style={[styles.statusText, { color: getStatusColor(invoice.status) }]}>
                              {invoice.status}
                            </Text>
                          </View>
                        </View>
                      </View>
                      <View style={styles.invoiceActions}>
                        <TouchableOpacity
                          onPress={() => handleSendInvoice(invoice)}
                          style={styles.invoiceActionBtn}
                          disabled={sendingInvoiceId === invoice.id}
                        >
                          {sendingInvoiceId === invoice.id ? (
                            <ActivityIndicator size="small" color={colors.primary} />
                          ) : (
                            <>
                              <Ionicons name="mail-outline" size={16} color={colors.primary} />
                              <Text style={styles.invoiceActionText}>{invoice.status === 'sent' ? 'Resend' : 'Send'}</Text>
                            </>
                          )}
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => handleEmailInvoice(invoice)}
                          style={styles.invoiceActionBtn}
                          disabled={sendingInvoiceId === invoice.id}
                        >
                          <Ionicons name="at-outline" size={16} color={colors.primary} />
                          <Text style={styles.invoiceActionText}>Email…</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => handleViewInvoice(invoice)} style={styles.invoiceActionBtn}>
                          <Ionicons name="document-outline" size={16} color={colors.primary} />
                          <Text style={styles.invoiceActionText}>PDF</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => handleMarkPaid(invoice)}
                          style={styles.invoiceActionBtn}
                          disabled={markingPaidId === invoice.id}
                        >
                          {markingPaidId === invoice.id ? (
                            <ActivityIndicator size="small" color={colors.success} />
                          ) : (
                            <>
                              <Ionicons name="checkmark-circle-outline" size={16} color={colors.success} />
                              <Text style={[styles.invoiceActionText, { color: colors.success }]}>Mark Paid</Text>
                            </>
                          )}
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => router.push(`/owner/fleet/edit-invoice?invoiceId=${invoice.id}` as never)} style={styles.invoiceActionBtn}>
                          <Ionicons name="pencil" size={16} color={colors.primary} />
                          <Text style={styles.invoiceActionText}>Edit</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => handleDeleteInvoice(invoice)} style={styles.invoiceActionBtn}>
                          <Ionicons name="trash-outline" size={16} color={colors.danger} />
                          <Text style={[styles.invoiceActionText, { color: colors.danger }]}>Delete</Text>
                        </TouchableOpacity>
                      </View>
                      {confirmAction?.type === 'sendInvoice' && confirmAction.id === invoice.id && (
                        <InlineConfirm
                          message={`Send invoice #${invoice.invoiceNumber} to the fleet's billing email?`}
                          visible={true}
                          onConfirm={doSendInvoice}
                          onCancel={() => setConfirmAction(null)}
                          confirmLabel="Send"
                          loading={confirmLoading}
                          variant="default"
                        />
                      )}
                      {confirmAction?.type === 'emailInvoice' && confirmAction.id === invoice.id && (
                        <InlineEmailPrompt
                          invoiceNumber={invoice.invoiceNumber}
                          value={emailTo}
                          onChangeText={setEmailTo}
                          error={emailError}
                          onSend={doEmailInvoice}
                          onCancel={() => setConfirmAction(null)}
                          loading={confirmLoading}
                        />
                      )}
                      {confirmAction?.type === 'markPaid' && confirmAction.id === invoice.id && (
                        <View style={{ marginTop: spacing.sm, padding: spacing.sm, backgroundColor: colors.surfaceSecondary, borderRadius: borderRadius.sm }}>
                          <Text style={{ fontSize: fontSize.sm, color: colors.text, marginBottom: spacing.sm }}>
                            Mark invoice #{invoice.invoiceNumber} as paid — how was it paid?
                          </Text>
                          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm }}>
                            {MARK_PAID_METHODS.map(m => (
                              <TouchableOpacity key={m} onPress={() => setMarkPaidMethod(m)}
                                style={{ paddingHorizontal: 12, paddingVertical: 4, borderRadius: 12, backgroundColor: markPaidMethod === m ? colors.primary : colors.surface, borderWidth: 1, borderColor: markPaidMethod === m ? colors.primary : colors.border }}>
                                <Text style={{ fontSize: fontSize.xs, color: markPaidMethod === m ? colors.white : colors.text }}>{m}</Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                          <TextInput
                            style={{ backgroundColor: colors.surface, borderRadius: 6, padding: 8, fontSize: fontSize.sm, color: colors.text, marginBottom: spacing.sm, borderWidth: 1, borderColor: colors.border }}
                            placeholder="Reference (check #, confirmation…) — optional"
                            placeholderTextColor={colors.textLight}
                            value={markPaidRef}
                            onChangeText={setMarkPaidRef}
                            maxLength={200}
                          />
                          <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                            <Button title="Cancel" onPress={() => setConfirmAction(null)} variant="ghost" size="sm" />
                            <Button title="Mark Paid" onPress={doMarkPaid} loading={confirmLoading} size="sm" />
                          </View>
                        </View>
                      )}
                      {confirmAction?.type === 'deleteInvoice' && confirmAction.id === invoice.id && (
                        <InlineConfirm
                          message={`Delete invoice #${invoice.invoiceNumber}? Linked transactions will be unlinked.`}
                          visible={true}
                          onConfirm={doDeleteInvoice}
                          onCancel={() => setConfirmAction(null)}
                          confirmLabel="Delete"
                          loading={confirmLoading}
                          variant="danger"
                        />
                      )}
                    </View>
                </Card>
              ))
            ) : (
              <Card style={styles.emptyCard}>
                <Text style={styles.emptyText}>{invSearch.trim() ? 'No invoices match your search' : 'No active invoices'}</Text>
              </Card>
            )
          )}

          {invoiceView === 'history' && (
            invoices.length > 0 ? (
              invoices.map(invoice => (
                <Card key={invoice.id} style={styles.itemCard}>
                  <View style={styles.invoiceRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.invoiceNumber}>#{invoice.invoiceNumber}</Text>
                      <Text style={styles.invoicePeriod}>
                        {formatDate(invoice.billingPeriodStart)} - {formatDate(invoice.billingPeriodEnd)}
                      </Text>
                      {invoice.paidAt ? (
                        <Text style={{ fontSize: fontSize.xs, color: colors.success, marginTop: 2 }}>
                          Paid {formatDate(invoice.paidAt)}{invoice.paymentMethod ? ` via ${invoice.paymentMethod}` : ''}{invoice.paymentReference ? ` (${invoice.paymentReference})` : ''}
                        </Text>
                      ) : null}
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                      <Text style={styles.invoiceAmount}>{fmt(invoice.totalAmount)}</Text>
                      {(invoice.discountAmount || 0) > 0 && (
                        <View style={{ alignItems: 'flex-end', marginTop: 2 }}>
                          <Text style={styles.invoiceSaved}>
                            Discount{invoice.discountPercentage ? ` (${invoice.discountPercentage}%)` : ''}: -{fmt(invoice.discountAmount)}
                          </Text>
                        </View>
                      )}
                      <View style={[styles.statusBadge, { backgroundColor: getStatusColor(invoice.status) + '20', marginTop: spacing.xs }]}>
                        <Text style={[styles.statusText, { color: getStatusColor(invoice.status) }]}>
                          {invoice.status}
                        </Text>
                      </View>
                    </View>
                  </View>
                  <View style={styles.invoiceActions}>
                    <TouchableOpacity onPress={() => handleViewInvoice(invoice)} style={styles.invoiceActionBtn}>
                      <Ionicons name="document-outline" size={16} color={colors.primary} />
                      <Text style={styles.invoiceActionText}>PDF</Text>
                    </TouchableOpacity>
                    {invoice.status === 'paid' && (
                      <TouchableOpacity
                        onPress={() => handleEmailInvoice(invoice)}
                        style={styles.invoiceActionBtn}
                        disabled={sendingInvoiceId === invoice.id}
                      >
                        <Ionicons name="at-outline" size={16} color={colors.primary} />
                        <Text style={styles.invoiceActionText}>Email…</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  {confirmAction?.type === 'emailInvoice' && confirmAction.id === invoice.id && (
                    <InlineEmailPrompt
                      invoiceNumber={invoice.invoiceNumber}
                      value={emailTo}
                      onChangeText={setEmailTo}
                      error={emailError}
                      onSend={doEmailInvoice}
                      onCancel={() => setConfirmAction(null)}
                      loading={confirmLoading}
                    />
                  )}
                </Card>
              ))
            ) : (
              <Card style={styles.emptyCard}>
                <Text style={styles.emptyText}>{invSearch.trim() ? 'No invoices match your search' : 'No invoice history'}</Text>
              </Card>
            )
          )}
        </>
      )}

      {activeTab === 'members' && (
        <>
          {/* Managers (multiple allowed) */}
          <Card style={styles.itemCard}>
            <Text style={styles.memberSectionTitle}>Fleet Managers</Text>
            {managerList.map(mgr => (
              <View key={mgr.id}>
                <View style={styles.managerRow}>
                  <View style={styles.managerIcon}>
                    <Ionicons name="shield-checkmark" size={24} color={colors.primary} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.memberName}>{titleCase(mgr.name)}</Text>
                    <Text style={styles.memberEmail}>#{mgr.accountNumber}</Text>
                  </View>
                  <TouchableOpacity onPress={() => handleRemoveManager(mgr)} style={styles.removeBtn}>
                    <Ionicons name="close-circle" size={24} color={colors.danger} />
                  </TouchableOpacity>
                </View>
                {confirmAction?.type === 'removeManager' && confirmAction.id === mgr.userId && (
                  <InlineConfirm
                    message={`Remove ${titleCase(mgr.name)} as manager?`}
                    visible={true}
                    onConfirm={doRemoveManager}
                    onCancel={() => setConfirmAction(null)}
                    confirmLabel="Remove"
                    loading={confirmLoading}
                    variant="danger"
                  />
                )}
              </View>
            ))}
            {managerList.length === 0 && <Text style={styles.noManagerText}>No manager assigned</Text>}
            <TextInput
              style={styles.searchInput}
              placeholder="Add manager — exact email, phone, or account #..."
              placeholderTextColor={colors.textLight}
              value={managerSearch}
              onChangeText={handleSearchManager}
              maxLength={100}
            />
            {searchingManager && <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.sm }} />}
            {managerResults.map(user => (
              <TouchableOpacity key={user.id} style={styles.searchResultRow} onPress={() => handleAssignManager(user.id)}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.memberName}>{titleCase(user.name)}</Text>
                  <Text style={styles.memberEmail}>#{user.accountNumber}</Text>
                </View>
                <Ionicons name="add-circle" size={24} color={colors.primary} />
              </TouchableOpacity>
            ))}
            {/* No matching account but the input is an email → offer an invite. */}
            {managerResults.length === 0 && !searchingManager && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(managerSearch.trim()) && (
              <Button
                title={inviting ? 'Inviting…' : `Invite ${managerSearch.trim().toLowerCase()} as manager`}
                size="sm"
                loading={inviting}
                onPress={() => handleInviteManager(managerSearch.trim().toLowerCase())}
                icon={<Ionicons name="mail-outline" size={16} color={colors.white} />}
                style={{ marginTop: spacing.sm }}
              />
            )}
            {managerInvites.length > 0 && (
              <View style={{ marginTop: spacing.md }}>
                <Text style={styles.memberSectionTitle}>Pending Manager Invites</Text>
                {managerInvites.map(inv => (
                  <View key={inv.id} style={styles.managerRow}>
                    <View style={styles.managerIcon}>
                      <Ionicons name="mail-unread-outline" size={22} color={colors.warning} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.memberName}>{inv.email}</Text>
                      <Text style={styles.memberEmail}>
                        Becomes manager on signup · expires {new Date(inv.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </Text>
                    </View>
                    <TouchableOpacity onPress={() => handleCancelInvite(inv.id)} style={styles.removeBtn} accessibilityLabel="Cancel invite">
                      <Ionicons name="close-circle" size={24} color={colors.danger} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </Card>

          {/* Members List */}
          <AdminToolbar
            search={memberSearch}
            onSearchChange={setMemberSearch}
            onSearch={() => fetchMembers(1).catch(() => {})}
            total={memberTotal}
            page={memberPage}
            limit={10}
            onPageChange={(p) => fetchMembers(p).catch(() => {})}
            csvEndpoint={`/api/fleet/accounts/${id}/members?role=member${memberSearch ? `&search=${encodeURIComponent(memberSearch)}` : ''}`}
            csvFilename="fleet-members.csv"
            label="member"
          />
          {members.length > 0 ? (
            members.map(member => (
              <Card key={member.id} style={styles.itemCard}>
                <View style={styles.memberRow}>
                  <View style={styles.memberIcon}>
                    <Ionicons name="person" size={20} color={colors.primary} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.memberName}>{titleCase(member.name)}</Text>
                    <Text style={styles.memberEmail}>#{member.accountNumber}</Text>
                  </View>
                  <TouchableOpacity onPress={() => handlePromoteMember(member)} style={styles.removeBtn} accessibilityLabel="Promote to manager">
                    <Ionicons name="shield-checkmark-outline" size={20} color={colors.primary} />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => handleRemoveMember(member)} style={styles.removeBtn}>
                    <Ionicons name="trash-outline" size={20} color={colors.danger} />
                  </TouchableOpacity>
                </View>
                {confirmAction?.type === 'removeMember' && confirmAction.id === member.userId && (
                  <InlineConfirm
                    message={`Remove ${titleCase(member.name)} from this fleet?`}
                    visible={true}
                    onConfirm={doRemoveMember}
                    onCancel={() => setConfirmAction(null)}
                    confirmLabel="Remove"
                    loading={confirmLoading}
                    variant="danger"
                  />
                )}
              </Card>
            ))
          ) : (
            <Card style={styles.emptyCard}>
              <Text style={styles.emptyText}>{memberSearch.trim() ? 'No members match your search' : 'No members yet'}</Text>
            </Card>
          )}
        </>
      )}

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>

      {/* Invoice Viewer Modal — HTML fetched from our own backend invoice renderer */}
      {Platform.OS === 'web' && (
        <Modal visible={!!invoiceHtml} transparent animationType="fade" onRequestClose={() => { setInvoiceHtml(null); setInvoiceUrl(null); }}>
          <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ backgroundColor: colors.surface, borderRadius: borderRadius.lg, width: '95%', maxWidth: 800, height: '85%', overflow: 'hidden' }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border }}>
                <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.text }}>Invoice</Text>
                <TouchableOpacity onPress={() => { setInvoiceHtml(null); setInvoiceUrl(null); }} hitSlop={8}>
                  <Ionicons name="close" size={24} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
              <ScrollView style={{ flex: 1 }}>
                <div style={{ padding: 16, backgroundColor: '#fff', color: '#1a1a1a', borderRadius: 8, minHeight: 200 }} dangerouslySetInnerHTML={{ __html: invoiceHtml ?? '' }} />
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}
    </ScrollView>
  );
}

/** InlineEmailPrompt - InlineConfirm-style inline box with a recipient TextInput
 *  plus Send/Cancel. Module-level on purpose: components defined inside another
 *  component's render remount (and wipe input state) on every pass. */
function InlineEmailPrompt({ invoiceNumber, value, onChangeText, error, onSend, onCancel, loading }: {
  readonly invoiceNumber: string;
  readonly value: string;
  readonly onChangeText: (text: string) => void;
  readonly error: string | null;
  readonly onSend: () => void;
  readonly onCancel: () => void;
  readonly loading: boolean;
}) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  return (
    <View style={styles.emailPrompt}>
      <View style={styles.emailPromptHeader}>
        <Ionicons name="mail-outline" size={18} color={colors.primary} />
        <Text style={styles.emailPromptTitle}>Email invoice #{invoiceNumber} to:</Text>
      </View>
      <TextInput
        style={styles.searchInput}
        placeholder="name@company.com"
        placeholderTextColor={colors.textLight}
        value={value}
        onChangeText={onChangeText}
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType="email-address"
        maxLength={320}
      />
      {error != null && <Text style={styles.emailPromptError}>{error}</Text>}
      <View style={styles.emailPromptActions}>
        <TouchableOpacity style={styles.emailCancelBtn} onPress={onCancel} disabled={loading}>
          <Text style={styles.emailCancelText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.emailSendBtn} onPress={onSend} disabled={loading}>
          {loading ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.emailSendText}>Send</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },
  errorText: { fontSize: fontSize.md, color: c.textSecondary },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  headerTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },

  infoCard: { marginBottom: spacing.md },
  infoHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: spacing.md },
  companyName: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  fleetType: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs },

  statusBadge: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
  },
  statusText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold },

  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: c.borderLight,
  },
  infoItem: { minWidth: '45%' },
  infoLabel: { fontSize: fontSize.xs, color: c.textLight, marginBottom: 2 },
  infoValue: { fontSize: fontSize.sm, color: c.text },

  actions: { flexDirection: 'row', gap: spacing.md, marginBottom: spacing.lg },

  tabs: {
    flexDirection: 'row',
    backgroundColor: c.surface,
    borderRadius: borderRadius.md,
    padding: spacing.xs,
    marginBottom: spacing.md,
  },
  tab: {
    flex: 1,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: borderRadius.sm,
  },
  activeTab: { backgroundColor: c.primary },
  tabText: { fontSize: fontSize.sm, color: c.textSecondary },
  activeTabText: { color: c.white, fontWeight: fontWeight.semibold },

  subTabs: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  subTab: {
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm,
    backgroundColor: c.surface,
    borderWidth: 1,
    borderColor: c.border,
  },
  subTabActive: {
    backgroundColor: c.primary,
    borderColor: c.primary,
  },
  subTabText: { fontSize: fontSize.sm, color: c.textSecondary },
  subTabTextActive: { color: c.white, fontWeight: fontWeight.semibold },

  addButton: { marginBottom: spacing.md },

  itemCard: { marginBottom: spacing.md },

  editForm: { paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: c.borderLight },
  editField: { marginBottom: spacing.sm },
  editLabel: { fontSize: fontSize.xs, color: c.textLight, marginBottom: 2 },
  editInput: {
    borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.sm,
    padding: spacing.sm, fontSize: fontSize.sm, color: c.text,
    backgroundColor: c.surface,
  },
  editActions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.md },

  txListCard: { marginBottom: spacing.md, paddingVertical: 0, paddingHorizontal: 0 },
  txRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  txRowBorder: { borderBottomWidth: 1, borderBottomColor: c.borderLight },
  txLeft: { flex: 1, marginRight: spacing.sm },
  txType: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  txSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  txRight: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  txAmount: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.success },

  refundedCard: { opacity: 0.6 },
  refundedAmount: { textDecorationLine: 'line-through', color: c.textLight },
  refundedBadge: {
    backgroundColor: c.danger + '20',
    paddingHorizontal: 6, paddingVertical: 1, borderRadius: borderRadius.sm,
  },
  refundedBadgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.danger },
  refundBtn: { padding: 4 },

  invoiceRow: { flexDirection: 'row', justifyContent: 'space-between' },
  invoiceNumber: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  invoicePeriod: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },
  invoiceAmount: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  invoiceSaved: { fontSize: fontSize.xs, color: c.success, fontWeight: fontWeight.medium },
  invoiceActions: {
    flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md, marginTop: spacing.sm,
    paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight,
  },
  invoiceActionBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  invoiceActionText: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.medium },

  emailPrompt: {
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: c.primary + '40',
    backgroundColor: c.primary + '08',
    padding: spacing.md,
    marginTop: spacing.sm,
  },
  emailPromptHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm },
  emailPromptTitle: { flex: 1, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  emailPromptError: { fontSize: fontSize.xs, color: c.danger, marginBottom: spacing.sm },
  emailPromptActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm },
  emailCancelBtn: {
    paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm, backgroundColor: c.surfaceSecondary,
  },
  emailCancelText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary },
  emailSendBtn: {
    paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm, minWidth: 80, alignItems: 'center',
    backgroundColor: c.primary,
  },
  emailSendText: { color: '#fff', fontSize: fontSize.sm, fontWeight: fontWeight.semibold },

  emptyCard: { alignItems: 'center', paddingVertical: spacing.xl },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary },

  memberSectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  managerRow: { flexDirection: 'row', alignItems: 'center' },
  managerIcon: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center',
    marginRight: spacing.md,
  },
  noManagerText: { fontSize: fontSize.sm, color: c.textLight, marginBottom: spacing.md },
  searchInput: {
    borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md,
    padding: spacing.sm, fontSize: fontSize.sm, color: c.text,
    backgroundColor: c.surface, marginBottom: spacing.sm,
  },
  searchResultRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  memberRow: { flexDirection: 'row', alignItems: 'center' },
  memberIcon: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center',
    marginRight: spacing.md,
  },
  memberName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  memberEmail: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },
  removeBtn: { padding: spacing.xs },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
